import { createRouter, createWebHistory } from "vue-router";
import { RouteRecordRaw } from "vue-router";
import TabsPage from "@/views/TabsPage.vue";
import { isUserAdmin, isUserDeveloper } from "@/services/authStore";
import { isOnboardingComplete, setOnboardingComplete } from "@/services/onboardingStatus";
import { goalsService } from "@/services/goalsService";
import { settingsMigration } from "@/services/settingsMigration";
import { initUserDb, clearActiveUser } from "@/services/storage";
import { bootstrapRxDb } from "@/services/rxdb/bootstrap";
import { hasStartupSynced, clearStartupSync } from "@/services/startupSync";
import { diaryRoutes } from "./routes/diary";
import { dashboardRoutes } from "./routes/dashboard";
import { foodsRoutes } from "./routes/foods";
import { settingsRoutes } from "./routes/settings";

// Track the active user so we can detect sign-out / user switch
let lastKnownUserId: string | null = null;

let routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: 'base',
    redirect: "/tabs/diary",
    meta: { public: true },
  },

  // Auth
  {
    path: "/signin",
    name: "sign-in",
    component: () => import("@/views/SignInPage.vue"),
    meta: { public: true },
  },
  {
    path: "/signin/oauth/callback",
    name: "oauth-callback",
    component: () => import("@/views/OAuthCallbackPage.vue"),
    meta: { public: true },
  },
  {
    path: "/auth/callback",
    redirect: (to) => ({
      path: "/signin/oauth/callback",
      query: to.query,
    }),
    meta: { public: true },
  },
  {
    path: "/coach-review/me",
    name: "coach-review-self",
    component: () => import("@/views/CoachReviewPage.vue"),
  },
  {
    path: "/coach-review/:token",
    name: "coach-review",
    component: () => import("@/views/CoachReviewPage.vue"),
    meta: { public: true },
  },

  // Onboarding — requires auth, skips onboarding + sync checks to avoid loops
  {
    path: "/onboarding",
    name: "onboarding",
    component: () => import("@/views/OnboardingWizard.vue"),
    meta: { skipOnboardingCheck: true, skipStartupSync: true },
  },

  // Startup sync loading screen — requires auth, skips onboarding + sync checks
  {
    path: "/syncing",
    name: "syncing",
    component: () => import("@/views/SyncingPage.vue"),
    meta: { skipOnboardingCheck: true, skipStartupSync: true },
  },

  {
    path: "/log-food-from-search",
    name: "log-food-from-search",
    component: () => import("@/views/LogFoodFromSearchPage.vue")
  },
  {
    path: "/foods/new",
    name: "food-create",
    component: () => import("@/views/CreateFoodPage.vue")
  },
  {
    path: "/foods/new/photos",
    name: "food-create-photos",
    component: () => import("@/views/CreateFoodFromPhotosPage.vue")
  },
  {
    path: "/foods/:id/edit",
    name: "food-edit",
    component: () => import("@/views/CreateFoodPage.vue")
  },
  {
    path: "/foods/:id",
    redirect: (to) => ({
      name: "food-detail",
      params: { id: to.params.id },
    }),
  },
  // Admin Routes (protected)
  {
    path: "/admin",
    name: "admin",
    redirect: { name: "admin-foods-review" },
    meta: { admin: true },
  },
  {
    path: "/admin/dashboard",
    name: "admin-dashboard",
    component: () => import("@/views/admin/AdminDashboardPage.vue"),
    meta: { admin: true },
  },
  {
    path: "/admin/users",
    name: "admin-users",
    component: () => import("@/views/admin/AdminUsersPage.vue"),
    meta: { admin: true },
  },
  {
    path: "/admin/users/:userId",
    name: "admin-user-detail",
    component: () => import("@/views/admin/AdminUserDetailPage.vue"),
    meta: { admin: true },
  },
  {
    path: "/admin/activity",
    name: "admin-activity",
    component: () => import("@/views/admin/AdminActivityPage.vue"),
    meta: { admin: true },
  },
  {
    path: "/admin/foods",
    name: "admin-foods-review",
    component: () => import("@/views/admin/AdminFoodsReviewPage.vue"),
    meta: { admin: true },
  },
  {
    path: "/admin/system-foods",
    name: "admin-system-foods",
    component: () => import("@/views/admin/AdminSystemFoodsSearchPage.vue"),
    meta: { admin: true },
  },

  // Tabs
  {
    path: "/tabs/",
    component: TabsPage,
    children: [
      {
        path: "",
        redirect: "/tabs/diary"
      },
      diaryRoutes,
      dashboardRoutes,
      foodsRoutes,
      settingsRoutes,
    ]
  }
];

// Test Pages (public)
if (import.meta.env.MODE === 'development') {
  routes = [...routes,
  {
    path: "/icon-test",
    name: "icon-test",
    component: () => import("@/views/IconTestPage.vue"),
    meta: { public: true },
  },
  {
    path: "/test-button",
    name: "test-button",
    component: () => import("@/views/ButtonTestPage.vue"),
    meta: { public: true },
  },
  {
    path: "/tests/components/base-card",
    name: "test-base-card",
    component: () => import("@/views/BaseCardTestPage.vue"),
    meta: { public: true },
  },
  {
    path: "/tests/components/base-modal",
    name: "test-base-modal",
    component: () => import("@/views/BaseModalTestPage.vue"),
    meta: { public: true },
  },
  {
    path: "/tests/components/form",
    name: "test-form",
    component: () => import("@/views/FormComponentsTestPage.vue"),
    meta: { public: true },
  }];
}

routes = [
  ...routes,
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundPage.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0, behavior: "auto" };
  },
})

router.afterEach(() => {
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    document.querySelectorAll<HTMLElement>(".page-content").forEach((element) => {
      element.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  });
});


router.beforeEach(async (to) => {
  const isPublic = to.meta.public ?? false;
  const isAdminPath = to.meta.admin ?? false;
  const isDeveloperPath = to.meta.developer ?? false;
  const { useAuthSessionStore } = await import("@/stores/authSessionStore");
  const authSessionStore = useAuthSessionStore();
  const session = await authSessionStore.ensureSession();

  if (session && session.user) {
    const userId = session.user.id;

    if (userId !== lastKnownUserId) {
      lastKnownUserId = userId;
      initUserDb(userId);
      // Run one-time Dexie → RxDB bootstrap migration
      await bootstrapRxDb(userId);
    }

    // Migrate old localStorage settings to Dexie (runs once per device, early-exits after)
    await settingsMigration.run();
  } else {
    if (lastKnownUserId !== null) {
      clearActiveUser();
      clearStartupSync();
      lastKnownUserId = null;
    }
  }

  if (isPublic) {
    return true;
  }

  // Step 1: Auth check
  if (!session || !session.user) {
    return {
      name: 'sign-in',
      query: { redirect: to.fullPath },
      replace: true,
    };
  }

  // Check admin privileges for admin paths
  if (isAdminPath && !isUserAdmin(session)) {
    return { name: 'base', replace: true };
  }

  // Check developer privileges for developer-only routes
  if (isDeveloperPath && !isUserDeveloper(session)) {
    return { name: 'tabs-settings', replace: true };
  }

  // Step 2: Startup sync gate — redirect to loading screen before first app access.
  // Must run BEFORE the onboarding check: on a second device the local RxDB is
  // empty, so checking onboarding before syncing always redirects to /onboarding
  // even when the user already completed it on their first device.
  const skipStartupSync = (to.meta.skipStartupSync ?? false) as boolean;
  if (!skipStartupSync && !hasStartupSynced(session.user.id)) {
    return { name: 'syncing', query: { redirect: to.fullPath }, replace: true };
  }

  // Step 3: Onboarding gate — runs after startup sync so server-side onboarding
  // state is available on second-device logins.
  const skipOnboardingCheck = to.meta.skipOnboardingCheck ?? false;
  if (!skipOnboardingCheck) {
    const onboardingCompleted = await isOnboardingComplete();
    if (!onboardingCompleted) {
      const activeGoal = await goalsService.getActiveGoal();
      if (activeGoal) {
        await setOnboardingComplete(); // returning user with local data, auto-complete
      } else {
        return { name: 'onboarding', replace: true };
      }
    }
  }

  return true;
});

export default router
